import { ListSectionHeader } from "@ui/components/internal/ListSectionHeader";
import { usePrefetchTriggers } from "@ui/hooks/use-prefetch-triggers";
import type { PrefetchProps } from "@ui/lib/prefetch-context";
import { tv } from "@ui/lib/tv";
import {
  Menu,
  MenuHeader,
  type MenuHeaderProps,
  MenuItem,
  type MenuItemProps,
  MenuPopover,
  type MenuPopoverProps,
  type MenuProps,
  MenuSection,
  type MenuSectionProps,
  MenuSeparator,
  type MenuSeparatorProps,
} from "@ui/primitives/Menu";
import { composeRenderProps, mergeProps } from "@ui/primitives/shared";
import type { ReactNode } from "react";

const dropMenuPopover = tv({
  base: ["rounded-3xl bg-white p-2", "shadow-md"],
});

const dropMenu = tv({
  base: "flex flex-col gap-1 outline-none",
});

const dropMenuItem = tv({
  base: [
    "flex cursor-pointer items-center gap-3 rounded-full px-2 py-1 text-base font-semibold text-neutral-600 outline-none",
    "data-[focused]:bg-neutral-50",
    "[&_svg]:size-5 [&_svg]:shrink-0",
  ],
});

const dropMenuSeparator = tv({
  base: "mx-2 my-1 h-px bg-neutral-100",
});

export interface DropMenuPopoverProps extends MenuPopoverProps {}

export function DropMenuPopover({ placement = "bottom start", ...props }: DropMenuPopoverProps) {
  return (
    <MenuPopover
      {...props}
      placement={placement}
      className={composeRenderProps(props.className, (className) => dropMenuPopover({ className }))}
    />
  );
}

export interface DropMenuProps<T extends object> extends MenuProps<T> {}

export function DropMenu<T extends object>(props: DropMenuProps<T>) {
  return <Menu {...props} className={composeRenderProps(props.className, (className) => dropMenu({ className }))} />;
}

export interface DropMenuItemProps extends MenuItemProps, Pick<PrefetchProps, "prefetch" | "cacheFor" | "cacheTags"> {
  icon?: ReactNode;
  /** Unread count; renders a trailing red badge (capped at 99+) when above zero. */
  badgeCount?: number;
}

export function DropMenuItem({ icon, badgeCount = 0, prefetch, cacheFor, cacheTags, ...props }: DropMenuItemProps) {
  const textValue = props.textValue ?? (typeof props.children === "string" ? props.children : undefined);
  const href = typeof props.href === "string" ? props.href : undefined;
  const { hoverHandlers, pressHandlers } = usePrefetchTriggers(href, { prefetch, cacheFor, cacheTags });
  // Computed outside JSX so the numeric cap isn't a translatable JSX literal.
  const badgeLabel = badgeCount > 99 ? "99+" : badgeCount;

  return (
    <MenuItem
      {...mergeProps(props, hoverHandlers ?? {}, pressHandlers ?? {})}
      textValue={textValue}
      className={composeRenderProps(props.className, (className, renderProps) =>
        dropMenuItem({ ...renderProps, className }),
      )}
    >
      {composeRenderProps(props.children, (children) => (
        <>
          {icon}
          {children}
          {badgeCount > 0 && (
            <span className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-400 px-1.5 text-xs font-bold text-white">
              {badgeLabel}
            </span>
          )}
        </>
      ))}
    </MenuItem>
  );
}

export interface DropMenuSeparatorProps extends MenuSeparatorProps {}

export function DropMenuSeparator({ className, ...props }: DropMenuSeparatorProps) {
  return <MenuSeparator {...props} className={dropMenuSeparator({ className })} />;
}

const dropMenuSection = tv({
  base: "mt-2 flex flex-col gap-1",
});

export interface DropMenuSectionProps<T extends object> extends MenuSectionProps<T> {}

export function DropMenuSection<T extends object>({ className, ...props }: DropMenuSectionProps<T>) {
  return <MenuSection {...props} className={dropMenuSection({ className })} />;
}

const dropMenuSectionHeader = tv({
  base: "px-2",
});

export interface DropMenuSectionHeaderProps extends MenuHeaderProps {}

export function DropMenuSectionHeader({ children, className, ...props }: DropMenuSectionHeaderProps) {
  return (
    <MenuHeader {...props} className={dropMenuSectionHeader({ className })}>
      <ListSectionHeader>{children}</ListSectionHeader>
    </MenuHeader>
  );
}
